/// REACT ///
import { useEffect } from "react";
import { useState } from "react";
/// HOOKS ///
import { useAppDispatch } from "api/hooks/apiHook";
import { useAppSelector } from "api/hooks/apiHook";
/// FUNCTIONS ///
import { handleUpdateCounter } from "handler/counter/update";
import { updateAngle } from "functions/sketch";
import { getFirstSlabInAreas } from "functions/sketch";
/// ACTIONS ///
import { updateLocalCounter } from "api/actions/sketch/counter";
import { restore } from "api/actions/history";
import { replaceRestorePoint } from "api/actions/history";
/// TYPES ///
import { Counter } from "api/types/sketch";
import { IHistoryActionType } from "api/types/history";
import { Slab } from "api/types/quote";
/// COMPONENTS ///
import UpdateDegrees from "components/quote/drawer/update_degrees";

interface Props {
  open: boolean,
  setOpen: (open: boolean) => void,
  original_value: number | null,
  point_uuid: string,
  selected_counter: Counter | null
}

export default function UpdateStoreDegrees({ open, setOpen, original_value, point_uuid, selected_counter }: Props) {
  const dispatch = useAppDispatch();
  const { areas } = useAppSelector(state => state.quote);
  const { cut_shapes } = useAppSelector(state => state.sketch);
  const [degrees, setDegrees] = useState<number>(0);
  const [error, setError] = useState<string>("");

  const update = (new_degrees: number) => {
    let adjusted_degrees: number = 0;

    if (selected_counter) {
      if (new_degrees < 22.5) {
        adjusted_degrees = 22.5;
        setError("Must be at least 22.5 degrees");
      }
      else if (new_degrees > 135) {
        adjusted_degrees = 135;
        setError("Must be less than 135 degrees");
      }
      else {
        adjusted_degrees = new_degrees;
        setError("");
      }

      setDegrees(new_degrees);

      const update_counter: Counter | null = updateAngle(selected_counter, point_uuid, adjusted_degrees);
      if (update_counter) {
        dispatch(updateLocalCounter(update_counter));
      }
    }
    else {
      setError("An error has occurred, please refresh the page and try again.");
    }
  }

  useEffect(() => {
    if (open && original_value) {
      setDegrees(original_value);
      dispatch(replaceRestorePoint(
        {
          undo_action: IHistoryActionType.UPDATE_COUNTER,
          desc: "Cancel Update Angle",
          undo_data: { ...selected_counter }
        }
      ));
    }
  }, [open]);

  const save = () => {
    if (selected_counter) {
      if (degrees < 22.5) {
        setError("Must be at least 22.5 degrees");
        return;
      }
      else if (degrees > 135) {
        setError("Must be less than 135 degrees");
        return;
      }

      const update_counter: Counter = updateAngle(selected_counter, point_uuid, degrees);

      const slab: Slab | null = getFirstSlabInAreas(areas, update_counter.area_uuid);
      handleUpdateCounter(
        dispatch,
        { ...selected_counter },
        update_counter,
        cut_shapes.filter(shape => shape.counter_uuid === selected_counter.uuid),
        slab.uuid,
        "Change Counter Angle"
      );
    }
    else {
      setError("An error has occurred, please refresh the page and try again.");
    }
    setOpen(false);
  }

  const cancel = () => {
    dispatch(restore());
    setOpen(false);
  }

  return (
    <UpdateDegrees
      open={open}
      setOpen={setOpen}
      degrees={degrees}
      setDegrees={update}
      error={error}
      save={save}
      cancel={cancel} />
  );
}
